/**
 * my-hiisi-versions.js — T-074 customer-side controller for the negotiation
 * timeline. Wires the Liquid-rendered `mht-negotiation` blocks to the
 * Worker App Proxy endpoints:
 *
 *   POST /apps/hiisi-bespoke/customer/version  — create counter-version
 *   POST /apps/hiisi-bespoke/customer/comment  — reply on any version
 *   POST /apps/hiisi-bespoke/customer/approve  — approve current Pole version
 *
 * Progressive enhancement: every form posts to the same App Proxy URL as
 * its `action` attribute, so JS-disabled clients still submit (Worker
 * returns 200 JSON; the next page load reflects the new state). JS adds:
 *
 *   - fetch + JSON body instead of native form submit
 *   - inline loading + error feedback (no full-page redirect)
 *   - post-success refetch + re-render of the timeline block (no reload,
 *     keeps scroll position; preserves any "in-flight" interactions on
 *     other bikes within the same order)
 *   - approve confirm dialog before POST (irreversible action gate)
 *
 * Refetch strategy: after a successful write, GET /customer/order, then
 * parse the response and POST a `?refresh=…` query to swap the inner
 * markup. Because the storefront isn't a SPA, we just call location.reload()
 * — the bike-card re-renders server-side from the freshly-written
 * metafields and the timeline picks up the new entry. Trade-off:
 *
 *   - Reload is simpler, no DOM-replace gymnastics
 *   - User loses scroll position; that's OK because new content is at
 *     the top of the timeline (newest first)
 *
 * Cost-leak (T-039): this script NEVER reads versions_extras-style
 * fields. The /customer/order endpoint excludes them server-side; even
 * if a future bug surfaced them, we don't have a code path that touches
 * them. The static source-grep test in tests/cost-leak-rendered-html
 * .spec.js confirms.
 */

(function () {
  'use strict';

  const APP_PROXY_BASE = '/apps/hiisi-bespoke';

  /** T-099 — cache-busting reload. window.location.reload() respects
   *  the storefront edge cache, so the customer's freshly-written
   *  approval (or counter, comment, etc.) wouldn't appear until the
   *  cache expired (~60-120s on Shopify's defaults). Adding a
   *  monotonic `_t` query param forces the edge to round-trip to the
   *  origin, picking up the metafield write immediately. We use
   *  location.replace so the previous URL doesn't pile up in browser
   *  history.
   *
   *  Param name `_t` (underscore-prefixed) signals "transient,
   *  cache-buster only" to anyone reading the URL — and the storefront
   *  Liquid templates ignore unknown query params.
   */
  function reloadWithCacheBust() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_t', String(Date.now()));
      window.location.replace(url.toString());
    } catch (_err) {
      // URL constructor failure on a very old browser — fall back to
      // best-effort reload. Cache may persist a few seconds but the
      // customer can retry.
      reloadWithCacheBust();
    }
  }

  /** Post JSON to an App Proxy endpoint. Returns the parsed response body
   *  on 2xx; throws an Error carrying the safeMessage when the Worker
   *  rejects. Storefront cookies + Shopify's signed App Proxy headers
   *  authenticate the request — no manual session-token wiring here. */
  async function postJson(path, body) {
    const res = await fetch(APP_PROXY_BASE + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(body),
    });
    let payload = null;
    try {
      payload = await res.json();
    } catch (_) {
      // Empty body or non-JSON — keep payload null, fall through to
      // status-based error.
    }
    if (!res.ok) {
      const msg =
        (payload && (payload.error || payload.safeMessage)) ||
        'HTTP ' + res.status;
      const err = new Error(msg);
      err.status = res.status;
      err.payload = payload;
      throw err;
    }
    return payload || {};
  }

  /** Pluck the timeline + form data attributes off the closest container.
   *  Returns null when the script is loaded but the page didn't render
   *  any timeline (e.g. legacy order — bike-card fell through to the
   *  proposals[lid] path). */
  function getTimelineCtx(formEl) {
    const root = formEl.closest('[data-mht-timeline]');
    if (!root) return null;
    return {
      root: root,
      orderId: root.getAttribute('data-mht-order-id') || '',
      lineItemId: root.getAttribute('data-mht-line-item-id') || '',
    };
  }

  /** Surface an error in the timeline's top-level alert region. Keeps
   *  the error visible until the customer dismisses it or triggers
   *  another action that clears it. */
  function showError(root, message) {
    const errEl = root.querySelector('[data-mht-error]');
    if (!errEl) {
      // Last-resort fallback when the markup is partial — alert keeps the
      // customer informed instead of silently swallowing the error.
      // eslint-disable-next-line no-alert
      window.alert(message);
      return;
    }
    errEl.textContent = message;
    errEl.hidden = false;
  }

  function clearError(root) {
    const errEl = root.querySelector('[data-mht-error]');
    if (errEl) {
      errEl.textContent = '';
      errEl.hidden = true;
    }
  }

  /** Set an inline status (per-form) so the customer sees "Sending…" /
   *  "Sent" feedback adjacent to the form they just submitted. */
  function setStatus(formEl, text, tone) {
    const statusEl =
      formEl.querySelector('[data-mht-status]') ||
      formEl.querySelector('[data-mht-suggest-status]');
    if (!statusEl) return;
    statusEl.textContent = text || '';
    statusEl.classList.remove(
      'mht-status--ok',
      'mht-status--err',
      'mht-status--busy',
    );
    if (tone === 'ok') statusEl.classList.add('mht-status--ok');
    else if (tone === 'err') statusEl.classList.add('mht-status--err');
    else if (tone === 'busy') statusEl.classList.add('mht-status--busy');
  }

  function disableForm(formEl, disabled) {
    const controls = formEl.querySelectorAll(
      'input, textarea, select, button',
    );
    controls.forEach(function (c) {
      c.disabled = disabled;
    });
  }

  // ---------------------------------------------------------------------------
  // Comment form — POST /customer/comment with envelope { order_id,
  // line_item_id, version_id, text }. T-074e: optimistic insert into the
  // thread list, no reload. The customer used to wait through a full page
  // refresh and (in prod) sometimes saw their comment "vanish" if the
  // storefront re-render returned a slightly stale metafield read.
  // Building the message-card client-side fixes both issues — staff sees
  // the comment via /staff/order on their next poll, customer sees it
  // immediately, and the server-side Liquid render picks it up unchanged
  // on the next natural page load.
  // ---------------------------------------------------------------------------
  async function handleCommentSubmit(e) {
    const formEl = e.currentTarget;
    e.preventDefault();
    const ctx = getTimelineCtx(formEl);
    if (!ctx) return; // Should not happen — defensive.

    const fd = new FormData(formEl);
    const text = String(fd.get('text') || '').trim();
    if (!text) return;
    const versionId = String(fd.get('version_id') || '');

    clearError(ctx.root);
    setStatus(formEl, 'Sending…', 'busy');
    disableForm(formEl, true);
    try {
      const resp = await postJson('/customer/comment', {
        order_id: ctx.orderId,
        line_item_id: ctx.lineItemId,
        version_id: versionId,
        text: text,
      });

      const savedAt =
        (resp && resp.saved_at) || new Date().toISOString();
      const threadList = ctx.root.querySelector('[data-mht-thread-list]');
      if (threadList) {
        const emptyHint = threadList.querySelector('.mht-thread-empty');
        if (emptyHint) emptyHint.remove();
        const msgEl = buildCustomerCommentDom(versionId, text, savedAt);
        threadList.appendChild(msgEl);
        msgEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      // T-074f: the customer just authored this comment — write its
      // saved_at to the per-version seen-key so the next page load
      // doesn't surface it as "unseen". `ensureLastSeenForVid` is
      // idempotent + monotonic (only writes when newer).
      ensureLastSeenForVid(ctx.orderId, ctx.lineItemId, versionId, savedAt);

      const textarea = formEl.querySelector('textarea');
      if (textarea) textarea.value = '';
      disableForm(formEl, false);
      setStatus(formEl, 'Sent ✓', 'ok');
      // Fade the status after a beat — the message itself is the receipt.
      window.setTimeout(function () {
        setStatus(formEl, '', null);
      }, 2500);
    } catch (err) {
      disableForm(formEl, false);
      setStatus(formEl, '', null);
      showError(ctx.root, 'Could not send comment: ' + err.message);
    }
  }

  /** Build a customer-authored thread message matching the Liquid render. */
  function buildCustomerCommentDom(versionId, text, isoDate) {
    const li = document.createElement('li');
    li.className = 'mht-thread-msg mht-thread-msg--customer';
    // T-074f: same data-* attrs the Liquid render emits so a later
    // re-init pass (or the unseen-tracker observing fresh DOM nodes)
    // treats the optimistic insert identically to a server-rendered
    // entry. Author-kind is hardcoded 'customer' since this DOM is only
    // built for the customer's own outbound message.
    li.setAttribute('data-mht-msg', '');
    li.setAttribute('data-vid', versionId);
    li.setAttribute('data-iso', isoDate);
    li.setAttribute('data-author-kind', 'customer');

    const head = document.createElement('div');
    head.className = 'mht-thread-msg-head';

    const author = document.createElement('span');
    author.className = 'mht-thread-msg-author';
    author.textContent = 'You';

    const tag = document.createElement('span');
    tag.className = 'mht-thread-msg-tag';
    tag.textContent = 'Re: ' + versionId;

    const date = document.createElement('span');
    date.className = 'mht-thread-msg-date';
    date.textContent = formatThreadDate(isoDate);

    head.appendChild(author);
    head.appendChild(tag);
    head.appendChild(date);

    const body = document.createElement('div');
    body.className = 'mht-thread-msg-text';
    body.textContent = text; // textContent escapes — CSS white-space handles newlines.

    li.appendChild(head);
    li.appendChild(body);
    return li;
  }

  // ---------------------------------------------------------------------------
  // T-074f — unseen-comment highlight.
  //
  // Customer comes back to /pages/my-hiisi and immediately wants to see
  // what's new. We track "last seen" per (orderId, lineItemId, versionId)
  // in localStorage; any comment whose `data-iso` exceeds the stored
  // value gets a `.mht-thread-msg--unseen` class on render. As soon as
  // the message scrolls into view, an IntersectionObserver writes the
  // iso back to localStorage — so the next reload won't highlight it.
  //
  // Customer's own messages (data-author-kind="customer") are skipped on
  // the highlight pass — they wrote it themselves, "new" reads as noise.
  // We still update localStorage on view so a Pole reply landing AFTER
  // a customer message doesn't get hidden by an older lastSeen value.
  // ---------------------------------------------------------------------------

  const LAST_SEEN_PREFIX = 'hiisi-customer-last-seen-comment';

  function lastSeenKey(orderId, lineItemId, versionId) {
    return (
      LAST_SEEN_PREFIX + '-' + orderId + '-' + lineItemId + '-' + versionId
    );
  }

  /** Read the stored iso for one (order, lineItem, vid). Returns '' on miss
   *  or when localStorage is unavailable (private-mode / quota / disabled). */
  function readLastSeen(orderId, lineItemId, versionId) {
    try {
      return (
        window.localStorage.getItem(
          lastSeenKey(orderId, lineItemId, versionId),
        ) || ''
      );
    } catch (_) {
      return '';
    }
  }

  /** Monotonic write — only updates when iso > stored. Swallows
   *  localStorage errors so the rest of the page keeps working in
   *  private-browsing / disabled-storage modes. */
  function ensureLastSeenForVid(orderId, lineItemId, versionId, iso) {
    if (!orderId || !lineItemId || !versionId || !iso) return;
    try {
      const key = lastSeenKey(orderId, lineItemId, versionId);
      const prev = window.localStorage.getItem(key) || '';
      if (iso > prev) window.localStorage.setItem(key, iso);
    } catch (_) {
      // Storage disabled — degrade gracefully (no persistence; highlight
      // re-fires every reload until storage comes back).
    }
  }

  /** Walk every msg under the timeline + flag the ones the customer
   *  hasn't seen yet. Wires an IntersectionObserver that writes
   *  localStorage when a msg enters view. */
  function initUnseenComments(root) {
    const orderId = root.getAttribute('data-mht-order-id') || '';
    const lineItemId = root.getAttribute('data-mht-line-item-id') || '';
    if (!orderId || !lineItemId) return;

    const msgs = root.querySelectorAll('[data-mht-msg]');
    if (!msgs.length) return;

    // Pre-pass: read each unique vid's lastSeen once so we don't call
    // localStorage.getItem N times for N comments in the same thread.
    const lastSeenCache = new Map();
    msgs.forEach(function (li) {
      const vid = li.getAttribute('data-vid') || '';
      if (!vid || lastSeenCache.has(vid)) return;
      lastSeenCache.set(vid, readLastSeen(orderId, lineItemId, vid));
    });

    msgs.forEach(function (li) {
      const vid = li.getAttribute('data-vid') || '';
      const iso = li.getAttribute('data-iso') || '';
      const kind = li.getAttribute('data-author-kind') || '';
      if (!vid || !iso) return;
      const prev = lastSeenCache.get(vid) || '';
      // Customer's own messages are never highlighted as "new" — they
      // just authored it. Pole staff messages get the highlight when
      // newer than the lastSeen iso.
      if (kind !== 'customer' && iso > prev) {
        li.classList.add('mht-thread-msg--unseen');
      }
    });

    // IntersectionObserver: stop highlighting once the customer has
    // actually looked at it. 0.4 threshold means ~40% visible, which a
    // short msg hits as soon as it enters the viewport.
    if (typeof window.IntersectionObserver !== 'function') {
      // Older browsers — fall back to a one-shot "mark all seen on first
      // load" pattern so we don't leave the highlight stuck forever.
      msgs.forEach(function (li) {
        const vid = li.getAttribute('data-vid') || '';
        const iso = li.getAttribute('data-iso') || '';
        ensureLastSeenForVid(orderId, lineItemId, vid, iso);
      });
      return;
    }

    const obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const li = entry.target;
          const vid = li.getAttribute('data-vid') || '';
          const iso = li.getAttribute('data-iso') || '';
          ensureLastSeenForVid(orderId, lineItemId, vid, iso);
          obs.unobserve(li);
        });
      },
      { threshold: 0.4 },
    );
    msgs.forEach(function (li) {
      obs.observe(li);
    });
  }

  /** Compact date — matches Liquid's `%-d %b %H:%M` for visual parity. */
  function formatThreadDate(iso) {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ];
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return d.getDate() + ' ' + months[d.getMonth()] + ' ' + hh + ':' + mm;
    } catch (_) {
      return '';
    }
  }

  // ---------------------------------------------------------------------------
  // Approve form — POST /customer/approve with envelope { order_id,
  // line_item_id, version_id, terms_version, payment_method }.
  //
  // T-097 — replaced the prior window.confirm() with a per-bike modal that
  // collects the payment_method choice ('invoice' default vs 'card' with
  // +2 % surcharge). The Worker accepts both values and defaults to
  // 'invoice' when the field is absent, so this JS path is the source of
  // truth for the customer's choice. The modal markup is rendered inline
  // in versions-timeline.liquid alongside each Pole version's approve
  // form so a single page-load delivers everything we need; no extra
  // network round-trip to load modal contents.
  //
  // Cost-leak: zero sensitive fields read or rendered. The modal shows
  // the customer-facing price + computed surcharge — both already on the
  // page elsewhere via data-hiisi-eur.
  // ---------------------------------------------------------------------------
  function findModalForForm(formEl) {
    // Approve form + modal live as siblings inside the same .mht-nego-actions
    // wrapper. Walk up to that wrapper, then find the modal inside.
    let cursor = formEl.parentElement;
    while (cursor) {
      const modal = cursor.querySelector('[data-mht-approve-modal]');
      if (modal && modal.parentElement === cursor) return modal;
      cursor = cursor.parentElement;
      if (!cursor || cursor === document.body) break;
    }
    return null;
  }

  function openApproveModal(modal) {
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    // Reset to the default 'invoice' radio on each open so a previous
    // session's lingering 'card' selection doesn't carry over silently.
    const invoiceRadio = modal.querySelector('input[data-mht-payment-radio][value="invoice"]');
    if (invoiceRadio) invoiceRadio.checked = true;
    // T-099 — reset the terms checkbox on each open so a customer who
    // ticked it, cancelled, then re-opened doesn't skip the gate.
    const termsBox = modal.querySelector('[data-mht-terms-checkbox]');
    if (termsBox) termsBox.checked = false;
    // T-099 — disable the confirm button on open until terms is ticked.
    const confirmBtn = modal.querySelector('[data-mht-approve-confirm]');
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.setAttribute('aria-disabled', 'true');
    }
    // Move focus into the modal for keyboard users — invoice radio is the
    // default, so it's the natural first stop.
    setTimeout(function () {
      if (invoiceRadio) invoiceRadio.focus();
    }, 0);
  }

  function closeApproveModal(modal) {
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
  }

  /** T-099 — terms checkbox gates the Approve & Continue button. Wired
   *  per modal in wireApproveModal so multiple bikes on one page each
   *  get their own gate without cross-talk. */
  function syncConfirmFromTerms(modal) {
    const termsBox = modal.querySelector('[data-mht-terms-checkbox]');
    const confirmBtn = modal.querySelector('[data-mht-approve-confirm]');
    if (!termsBox || !confirmBtn) return;
    const allow = !!termsBox.checked;
    confirmBtn.disabled = !allow;
    confirmBtn.setAttribute('aria-disabled', allow ? 'false' : 'true');
  }

  function getSelectedPaymentMethod(modal) {
    const checked = modal.querySelector('input[data-mht-payment-radio]:checked');
    if (!checked) return 'invoice';
    const v = checked.value;
    return v === 'card' ? 'card' : 'invoice';
  }

  async function handleApproveSubmit(e) {
    const formEl = e.currentTarget;
    e.preventDefault();
    const modal = findModalForForm(formEl);
    if (!modal) {
      // No modal on the page — degraded path. Fall back to window.confirm
      // so the customer can still approve (Worker accepts an envelope
      // without payment_method and defaults to 'invoice').
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Approve this build? Once approved, Invoice 1 will be issued within ' +
          '2 business days and you can no longer counter-propose this bike.',
      );
      if (!ok) return;
      submitApprove(formEl, 'invoice');
      return;
    }
    openApproveModal(modal);
  }

  /** T-097 — collect choice + actually POST. Called from the modal's
   *  Confirm button or the degraded window.confirm fallback. */
  async function submitApprove(formEl, paymentMethod) {
    const ctx = getTimelineCtx(formEl);
    if (!ctx) return;
    const fd = new FormData(formEl);
    const versionId = String(fd.get('version_id') || '');
    const termsVersion = String(fd.get('terms_version') || 'v1.0');
    // Mirror the choice back into the hidden form input so a future
    // form-submit (e.g. a no-JS fallback in some future browser env)
    // carries the chosen payment_method too.
    const hidden = formEl.querySelector('[data-mht-payment-method-input]');
    if (hidden) hidden.value = paymentMethod;

    clearError(ctx.root);
    disableForm(formEl, true);
    try {
      await postJson('/customer/approve', {
        order_id: ctx.orderId,
        line_item_id: ctx.lineItemId,
        version_id: versionId,
        terms_version: termsVersion,
        payment_method: paymentMethod,
      });
      // Immediate UX confirmation — Shopify storefront edge cache can return
      // pre-approval HTML on reloadWithCacheBust(), leaving the customer
      // staring at the same Approve button after their click. Replace the
      // approve-form area with a clear "Approved" banner BEFORE reloading so
      // the customer sees confirmation regardless of what the reloaded page
      // renders. The reload still runs after a short delay to refresh the
      // rest of the page state (banners, next-step CTAs) when storefront
      // cache eventually invalidates.
      try {
        const formContainer = formEl.parentElement;
        if (formContainer) {
          const now = new Date();
          const hh = String(now.getHours()).padStart(2, '0');
          const mm = String(now.getMinutes()).padStart(2, '0');
          const banner = document.createElement('div');
          banner.className = 'mht-approve-confirmation';
          banner.setAttribute('role', 'status');
          banner.setAttribute('aria-live', 'polite');
          banner.style.padding = '1rem';
          banner.style.background = 'rgba(40, 160, 80, 0.12)';
          banner.style.border = '1px solid rgba(40, 160, 80, 0.4)';
          banner.style.borderRadius = '8px';
          banner.style.marginTop = '1rem';
          banner.textContent = '✓ Approved at ' + hh + ':' + mm + '. Refreshing…';
          formContainer.innerHTML = '';
          formContainer.appendChild(banner);
        }
      } catch (_uiErr) {
        // Defensive — never let UI hiccup block the reload below.
      }
      setTimeout(reloadWithCacheBust, 1500);
    } catch (err) {
      disableForm(formEl, false);
      const msg =
        err.status === 409 && err.payload && err.payload.error === 'already_approved'
          ? 'This bike is already approved.'
          : 'Could not approve: ' + err.message;
      showError(ctx.root, msg);
    }
  }

  /** T-097 — wire the modal Cancel + Confirm buttons. Each modal sits
   *  next to exactly one approve form; we resolve back to the form via
   *  the parent .mht-nego-actions wrapper.
   *  T-099 — also wire the Bespoke Delivery Terms checkbox so the confirm
   *  button stays disabled until accepted. Defence in depth: even if a
   *  user mutates the disabled attribute via devtools, the click handler
   *  re-checks the checkbox state before submitting. */
  function wireApproveModal(modal) {
    if (modal.dataset.mhtApproveModalWired === '1') return;
    modal.dataset.mhtApproveModalWired = '1';
    const formEl = modal.parentElement
      ? modal.parentElement.querySelector('[data-mht-approve-form]')
      : null;
    const cancelBtn = modal.querySelector('[data-mht-approve-cancel]');
    const confirmBtn = modal.querySelector('[data-mht-approve-confirm]');
    const termsBox = modal.querySelector('[data-mht-terms-checkbox]');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', function () {
        closeApproveModal(modal);
      });
    }
    if (termsBox) {
      termsBox.addEventListener('change', function () {
        syncConfirmFromTerms(modal);
      });
    }
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        if (!formEl) return;
        // T-099 — final gate. Reject if terms isn't ticked, even if the
        // button was somehow enabled by devtools tinkering.
        if (termsBox && !termsBox.checked) {
          syncConfirmFromTerms(modal);
          return;
        }
        const paymentMethod = getSelectedPaymentMethod(modal);
        closeApproveModal(modal);
        void submitApprove(formEl, paymentMethod);
      });
    }
    // Backdrop click = cancel. Don't dismiss when the click bubbled up
    // from inside the modal dialog itself — only an actual backdrop hit.
    modal.addEventListener('click', function (ev) {
      if (ev.target === modal) closeApproveModal(modal);
    });
    // Escape key — close. Captured on the document while modal is open
    // and removed on close to keep listeners minimal.
    function onKey(ev) {
      if (ev.key === 'Escape' && !modal.hidden) {
        closeApproveModal(modal);
      }
    }
    document.addEventListener('keydown', onKey);
  }

  // ---------------------------------------------------------------------------
  // Suggest-changes form — POST /customer/version. The Liquid form posts a
  // flat name="spec_size" etc. set; we reconstruct the entry.spec object
  // before sending. Empty / blank fields are dropped so narrowBikeSpec
  // doesn't drop them silently downstream — staff sees only the deltas
  // the customer actually set.
  // ---------------------------------------------------------------------------
  function buildSpecFromForm(fd) {
    const spec = {};
    // Frame fields — kept verbatim when present (Worker narrowBikeSpec
    // will validate enums and drop invalid values).
    const name = String(fd.get('spec_name') || '').trim();
    if (name) spec.name = name;
    const buildType = String(fd.get('spec_buildType') || '').trim();
    if (buildType) spec.buildType = buildType;
    const size = String(fd.get('spec_size') || '').trim();
    if (size) spec.size = size;
    const wheel = String(fd.get('spec_wheel') || '').trim();
    if (wheel) spec.wheel = wheel;
    const travel = String(fd.get('spec_travel') || '').trim();
    if (travel) spec.travel = travel;
    const color = String(fd.get('spec_color') || '').trim();
    if (color) spec.color = color;

    // customComponents map — built from any spec_cc_<key> field that has
    // a non-blank value. Keep the keys lowercase / canonical so the
    // Worker side sees what staff expects.
    const cc = {};
    const ccKeys = [
      'fork', 'shock', 'brakes', 'wheels', 'tires', 'cockpit',
      'stem', 'bars', 'grips', 'dropper', 'drivetrain', 'saddle',
    ];
    ccKeys.forEach(function (k) {
      const v = String(fd.get('spec_cc_' + k) || '').trim();
      if (v) cc[k] = v;
    });
    if (Object.keys(cc).length > 0) spec.customComponents = cc;

    return spec;
  }

  // ---------------------------------------------------------------------------
  // T-116 — Suggest-form draft persistence via localStorage.
  //
  // Auto-save on every input/change event in the suggest form; restore on form
  // open; clear on successful submit. Cancel is a no-op (the draft survives
  // an accidental close).
  //
  // Key: mht-suggest-draft:<orderId>:<lineItemId>:<basedOnVersionId>
  //   The basedOnVersionId segment ties the draft to the Pole version the
  //   customer was responding to. When Pole posts a new version, the form's
  //   data-mht-source-version-id changes → the old draft's key no longer
  //   matches → the form starts fresh without stale text leaking in.
  //
  // Defensive: every localStorage call is wrapped in try/catch. Private mode,
  // quota exhaustion, and storage-disabled-by-policy must never break the
  // form. Auto-save is a UX nice-to-have, not a correctness guarantee.
  // ---------------------------------------------------------------------------
  function suggestDraftKey(form) {
    const ctx = getTimelineCtx(form);
    if (!ctx || !ctx.orderId || !ctx.lineItemId) return null;
    const basedOn = (form.dataset && form.dataset.mhtSourceVersionId) || '';
    return 'mht-suggest-draft:' + ctx.orderId + ':' + ctx.lineItemId + ':' + basedOn;
  }

  function saveSuggestDraft(form) {
    try {
      const key = suggestDraftKey(form);
      if (!key) return;
      const fd = new FormData(form);
      const data = {};
      const it = fd.entries();
      let step = it.next();
      while (!step.done) {
        const name = step.value[0];
        const value = step.value[1];
        // FormData yields File for file inputs; localStorage round-trips only
        // strings. The suggest form has no file inputs today, but the guard
        // keeps a future addition from silently emitting `[object File]`.
        if (typeof value === 'string') data[name] = value;
        step = it.next();
      }
      window.localStorage.setItem(key, JSON.stringify(data));
    } catch (_) {
      // Disabled storage / over quota / private mode — silently ignore.
    }
  }

  function applyDraftValue(form, name, value) {
    if (typeof name !== 'string' || typeof value !== 'string') return;
    // Skip hidden infrastructure fields (order_id, line_item_id, spec_version,
    // entry_based_on) — those are server-rendered and must reflect the current
    // form context, not whatever the draft was based on. The basedOn segment
    // of the key already gates cross-version pollution.
    const inputs = form.querySelectorAll('[name="' + name + '"]:not([type="hidden"])');
    inputs.forEach(function (el) {
      if (el.tagName === 'SELECT') {
        // Only assign if the option exists in the current select — protects
        // against catalog-filter drift (e.g. travel change pruned the saved
        // component option). Without the guard, an unknown value leaves the
        // select blank instead of falling back to "Keep Pole's pick".
        const has = Array.prototype.some.call(el.options, function (o) { return o.value === value; });
        if (has) el.value = value;
      } else if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        el.value = value;
      }
    });
  }

  function restoreSuggestDraft(form) {
    try {
      const key = suggestDraftKey(form);
      if (!key) return;
      const raw = window.localStorage.getItem(key);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return;

      // Pass 1: restore frame fields, name, and notes (everything except
      // component selects). Dispatch synthetic change events on each frame
      // control so initSideBySide's handlers refresh the hero image, the
      // price tile, and (for travel/wheel) rebuild the component rows — so
      // the component selects exist before pass 2 pins their saved values.
      Object.keys(data).forEach(function (name) {
        if (name.indexOf('spec_cc_') === 0) return;
        applyDraftValue(form, name, data[name]);
      });
      form.querySelectorAll('[data-mht-sbs-draft]').forEach(function (el) {
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Pass 2: restore component selections. By now they either exist from
      // initSideBySide's first-paint render or were just rebuilt by the
      // travel/wheel dispatch above. Dispatch change on each so the row's
      // own listener flips .is-changed and recomputes the price tile.
      Object.keys(data).forEach(function (name) {
        if (name.indexOf('spec_cc_') !== 0) return;
        applyDraftValue(form, name, data[name]);
      });
      form.querySelectorAll('[data-mht-slot-select]').forEach(function (el) {
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } catch (_) {
      // Corrupt JSON or storage error — fall through to server defaults.
    }
  }

  function clearSuggestDraft(form) {
    try {
      const key = suggestDraftKey(form);
      if (!key) return;
      window.localStorage.removeItem(key);
    } catch (_) { /* ignore */ }
  }

  async function handleSuggestSubmit(e) {
    const formEl = e.currentTarget;
    e.preventDefault();
    const ctx = getTimelineCtx(formEl);
    if (!ctx) return;

    const fd = new FormData(formEl);
    const spec = buildSpecFromForm(fd);
    const specVersion = String(fd.get('spec_version') || '2.7');
    const notes = String(fd.get('entry_notes') || '').trim();
    const basedOn = String(fd.get('entry_based_on') || '').trim();

    // Soft validate: at least one of spec / notes must be non-empty. The
    // Worker would 400 a fully-empty spec, but stopping client-side is
    // friendlier than waiting for the round trip.
    const hasSpecEdit = Object.keys(spec).length > 0;
    if (!hasSpecEdit && !notes) {
      setStatus(formEl, 'Add a change or a note before sending.', 'err');
      return;
    }

    const entry = { spec: spec, spec_version: specVersion };
    if (notes) entry.notes = notes;
    if (basedOn) entry.based_on = basedOn;

    clearError(ctx.root);
    setStatus(formEl, 'Sending…', 'busy');
    disableForm(formEl, true);
    try {
      await postJson('/customer/version', {
        order_id: ctx.orderId,
        line_item_id: ctx.lineItemId,
        entry: entry,
      });
      setStatus(formEl, 'Sent. Refreshing…', 'ok');
      // T-116 — version landed server-side; the saved draft has served its
      // purpose. Clear before the reload so a future open starts fresh.
      clearSuggestDraft(formEl);
      reloadWithCacheBust();
    } catch (err) {
      disableForm(formEl, false);
      const msg =
        err.status === 409 && err.payload && err.payload.error === 'already_approved'
          ? 'This bike has been approved — counter-proposals are locked.'
          : err.status === 409 && err.payload && err.payload.error === 'too_many_versions'
            ? 'Conversation limit reached. Please continue via care@polebicycles.com.'
            : 'Could not send your version: ' + err.message;
      setStatus(formEl, '', null);
      showError(ctx.root, msg);
    }
  }

  // ---------------------------------------------------------------------------
  // "Suggest changes" / "Edit my proposal" toggle — opens the per-bike
  // form below the timeline + scrolls it into view. The form is rendered
  // server-side with `hidden`; we flip the attribute + update aria.
  // ---------------------------------------------------------------------------
  function handleSuggestToggle(e) {
    const btn = e.currentTarget;
    const ctx = getTimelineCtx(btn);
    if (!ctx) return;
    const wrap = ctx.root.querySelector('[data-mht-suggest-wrap]');
    if (!wrap) return;
    const isOpen = !wrap.hidden;
    if (isOpen) {
      wrap.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      return;
    }
    wrap.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    // T-091 — hydrate the side-by-side editor on first open: wire the
    // live-hero swap, the price-impact tile, and the deferred component
    // catalog render. Idempotent — repeated calls only re-bind once.
    const form = wrap.querySelector('[data-mht-suggest-form]');
    if (form) initSideBySide(form);
    // T-116 — rehydrate any persisted draft for this (order, line item,
    // basedOn) tuple. Runs after initSideBySide so the component rows
    // exist before pass 2 of the restore pins their saved selections.
    if (form) restoreSuggestDraft(form);
    if (form && typeof form.scrollIntoView === 'function') {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    const firstField = wrap.querySelector('input:not([type="hidden"]), select, textarea');
    if (firstField) firstField.focus();
  }

  function handleSuggestCancel(e) {
    const btn = e.currentTarget;
    const ctx = getTimelineCtx(btn);
    if (!ctx) return;
    const wrap = ctx.root.querySelector('[data-mht-suggest-wrap]');
    if (!wrap) return;
    wrap.hidden = true;
    // Reset aria-expanded on whichever button last opened it. We don't
    // track the trigger explicitly — querying all matches is cheap and
    // keeps the state synced.
    ctx.root
      .querySelectorAll('[data-mht-action="suggest-changes"]')
      .forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
  }

  // ---------------------------------------------------------------------------
  // T-091 — side-by-side suggest editor.
  //
  // Hydrates the server-rendered scaffold in my-hiisi-version-suggest-form.
  // Three responsibilities:
  //   1. Live hero swap on color/size/wheel/travel — mirrors the
  //      configurator's behaviour so the customer's draft feels real.
  //   2. Component rows — lazily populated from window.HIISI_COMPONENTS_DATA
  //      the first time the <details> is expanded. Per-slot <select>
  //      filtered by current travel value (parity with the demo's
  //      optionsForSlot).
  //   3. Live price impact tile — sums Pole's totals.priceEur + the
  //      msrp delta of any swapped components vs Pole's pick.
  //
  // Cost-leak: HIISI_COMPONENTS_DATA is the PUBLIC bundle written by
  // scripts/sync-pricing.py — never includes sourcing tier / cost / margin.
  // The Worker side's components-sourcing.ts is a separate file that
  // never ships to the browser.
  // ---------------------------------------------------------------------------

  const HERO_KEYS = ['color', 'size', 'wheel', 'travel'];

  function initSideBySide(form) {
    if (!form || form.dataset.mhtSbsBound === 'true') return;
    form.dataset.mhtSbsBound = 'true';

    const heroImg = form.querySelector('[data-mht-suggest-hero-img]');
    const draftPriceEl = form.querySelector('[data-mht-price-impact-draft]');
    const deltaWrap = form.querySelector('[data-mht-price-impact-delta]');
    const polePriceRaw = parseFloat(form.dataset.mhtPolePrice || '0');
    const polePrice = Number.isFinite(polePriceRaw) ? polePriceRaw : 0;

    // Parse Pole's full spec once. Liquid escaped the JSON so we read
    // textContent via a DOMParser-free path — the value is on the form
    // dataset which the browser unescapes for us already.
    let poleSpec = {};
    try {
      poleSpec = JSON.parse(form.dataset.mhtPoleSpec || '{}');
    } catch (_) {
      poleSpec = {};
    }

    function currentDraftFrame() {
      return {
        buildType: getDraftValue(form, 'buildType') || 'full',
        size:      getDraftValue(form, 'size')      || '',
        wheel:     getDraftValue(form, 'wheel')     || '29',
        travel:    getDraftValue(form, 'travel')    || '',
        color:     getDraftValue(form, 'color')     || '',
      };
    }

    function refreshHero() {
      if (!heroImg) return;
      const f = currentDraftFrame();
      if (!f.travel || !f.size || !f.color || !f.wheel) return;
      let key = f.travel + '-';
      if (f.wheel === 'Mullet') key += 'Mullet-';
      key += f.size + '-' + f.color;
      // Asset URL pattern matches Liquid's `<key>.webp | asset_url` — same
      // origin, /cdn/ path. Swap the filename in place rather than building
      // the absolute URL, so the CDN base stays consistent across themes.
      const baseSrc = heroImg.src;
      heroImg.src = baseSrc.replace(/\/[^/]+\.webp(\?[^/]*)?$/, '/' + key + '.webp$1');
    }

    function recomputePrice() {
      let draftPrice = polePrice;
      // Sum custom-component price delta vs Pole's selection.
      const slotEls = form.querySelectorAll('[data-mht-slot-select]');
      slotEls.forEach(function (sel) {
        const draftId = sel.value;
        const poleId = sel.dataset.poleValue || '';
        if (!draftId || draftId === poleId) return;
        const draftComp = findComponent(draftId);
        const poleComp = poleId ? findComponent(poleId) : null;
        if (!draftComp) return;
        const draftMsrp = Number(draftComp.msrp) || 0;
        const poleMsrp = poleComp ? Number(poleComp.msrp) || 0 : 0;
        draftPrice += draftMsrp - poleMsrp;
      });
      // Update DOM with the new total + delta tile.
      if (draftPriceEl) {
        draftPriceEl.setAttribute('data-hiisi-eur', String(Math.round(draftPrice)));
        // Strip any prior hydration marker so HiisiCurrency.hydrate re-runs.
        draftPriceEl.removeAttribute('data-hiisi-hydrated');
      }
      if (deltaWrap) {
        const delta = Math.round(draftPrice - polePrice);
        const strong = deltaWrap.querySelector('strong');
        deltaWrap.classList.remove('is-up', 'is-down', 'is-same');
        if (strong) {
          if (delta === 0) {
            strong.textContent = '= same';
            deltaWrap.classList.add('is-same');
          } else if (delta > 0) {
            strong.textContent = '+ €' + delta.toLocaleString('en-IE');
            deltaWrap.classList.add('is-up');
          } else {
            strong.textContent = '− €' + Math.abs(delta).toLocaleString('en-IE');
            deltaWrap.classList.add('is-down');
          }
        }
      }
      // Re-run HiisiCurrency hydration so the ≈local estimate tail tracks.
      if (window.HiisiCurrency && typeof window.HiisiCurrency.hydrate === 'function') {
        window.HiisiCurrency.hydrate(form);
      }
    }

    function handleFrameChange(ev) {
      const key = ev.target.getAttribute('data-mht-sbs-draft');
      if (!key) return;
      if (HERO_KEYS.indexOf(key) !== -1) refreshHero();
      // Frame fields don't affect base price in the current pricing model
      // (color upcharge is rolled into Pole's totals.priceEur). Still
      // recompute to keep the tile correct after component swaps that
      // are dependent on travel.
      if (key === 'travel' || key === 'wheel') {
        rebuildComponentRows(form, poleSpec);
      }
      recomputePrice();
    }

    // Bind every draft control. Falls back to plain change events — the
    // demo's input/onChange semantics map straight onto storefront <select>
    // and <input type="text">.
    form.querySelectorAll('[data-mht-sbs-draft]').forEach(function (ctrl) {
      ctrl.addEventListener('change', handleFrameChange);
      // Text inputs (travel) also need input events for live feedback.
      if (ctrl.tagName === 'INPUT' && ctrl.type === 'text') {
        ctrl.addEventListener('input', handleFrameChange);
      }
    });

    // T-093 parity fix — render component rows immediately when the
    // suggest form mounts. Demo renders all 16 slots side-by-side from
    // first paint; the prior <details> lazy-load hid the affordance
    // customers came here to use. The catalog is the public bundle
    // (HIISI_COMPONENTS_DATA) loaded with `defer` regardless, so
    // pageweight is unchanged.
    //
    // The section element (no longer a <details>) carries
    // [data-mht-component-section] + a [data-mht-rendered] sentinel so
    // re-hydration is a no-op on rapid open/close of the parent form.
    const componentSection = form.querySelector('[data-mht-component-section]');
    if (componentSection && !componentSection.dataset.mhtRendered) {
      rebuildComponentRows(form, poleSpec);
      componentSection.dataset.mhtRendered = 'true';
    }

    // First render of the price-impact tile — sums to zero until the
    // customer touches anything, but keeps the DOM in a consistent state.
    recomputePrice();
  }

  function getDraftValue(form, key) {
    const el = form.querySelector('[data-mht-sbs-draft="' + key + '"]');
    return el ? String(el.value || '').trim() : '';
  }

  function findComponent(id) {
    const catalog = window.HIISI_COMPONENTS_DATA;
    if (!catalog || !Array.isArray(catalog)) return null;
    for (let i = 0; i < catalog.length; i++) {
      if (catalog[i].id === id) return catalog[i];
    }
    return null;
  }

  /** Slots the customer can suggest swaps for. Matches SLOT_META in
   *  apps/hiisi-bespoke/src/pricing.ts (drive-system excluded since it
   *  isn't a customer choice — it's the standard Maxon kit). */
  const SLOT_DEFS = [
    { id: 'fork',       category: 'Fork',       section: 'Suspension' },
    { id: 'shock',      category: 'Rear shock', section: 'Suspension' },
    { id: 'cranks',     category: 'Cranks',     section: 'Drivetrain' },
    { id: 'chain',      category: 'Chain',      section: 'Drivetrain' },
    { id: 'cassette',   category: 'Cassette',   section: 'Drivetrain' },
    { id: 'derailleur', category: 'Derailleur', section: 'Drivetrain' },
    { id: 'shifter',    category: 'Shifter',    section: 'Drivetrain' },
    { id: 'brakes',     category: 'Brakes',     section: 'Brakes' },
    { id: 'wheels',     category: 'Wheels',     section: 'Wheels & tires' },
    { id: 'tires',      category: 'Tires',      section: 'Wheels & tires' },
    { id: 'handlebar',  category: 'Handlebar',  section: 'Cockpit' },
    { id: 'stem',       category: 'Stem',       section: 'Cockpit' },
    { id: 'grips',      category: 'Grips',      section: 'Cockpit' },
    { id: 'headset',    category: 'Headset',    section: 'Cockpit' },
    { id: 'saddle',     category: 'Saddle',     section: 'Saddle & seat post' },
    { id: 'seatpost',   category: 'Seat post',  section: 'Saddle & seat post' },
  ];

  function rebuildComponentRows(form, poleSpec) {
    const rowsEl = form.querySelector('[data-mht-component-rows]');
    if (!rowsEl) return;
    const catalog = window.HIISI_COMPONENTS_DATA;
    if (!catalog || !Array.isArray(catalog)) {
      // Catalog hasn't loaded yet — leave the "Loading…" copy in place.
      return;
    }
    // Clear everything except the header row.
    const oldRows = rowsEl.querySelectorAll('.mht-sbs-row, .mht-sbs-section-title, .mht-suggest-components__loading, .mht-sbs-empty');
    oldRows.forEach(function (n) { n.remove(); });

    const travelStr = getDraftValue(form, 'travel');
    const travel = parseInt(travelStr, 10);
    const wheel = getDraftValue(form, 'wheel');
    const buildType = getDraftValue(form, 'buildType');
    const polePicks = (poleSpec && poleSpec.customComponents) || {};
    let sectionCursor = null;
    let renderedAnyRow = false;

    SLOT_DEFS.forEach(function (slot) {
      // Frameset only carries shock as a swappable slot — same gate as
      // the configurator's frameset preset.
      if (buildType === 'frameset' && slot.id !== 'shock') return;
      // Filter catalog options for this slot. Travel-compat is enforced
      // when the component declares compatibleTravels; otherwise the
      // component fits any frame.
      const options = catalog.filter(function (c) {
        if (c.category !== slot.id) return false;
        if (Array.isArray(c.compatibleTravels) && c.compatibleTravels.length > 0) {
          return Number.isFinite(travel) ? c.compatibleTravels.indexOf(travel) !== -1 : true;
        }
        return true;
      });
      if (!options.length) return;

      // Section header — render once per `section` value.
      if (sectionCursor !== slot.section) {
        const head = document.createElement('div');
        head.className = 'mht-sbs-section-title';
        head.textContent = slot.section;
        rowsEl.appendChild(head);
        sectionCursor = slot.section;
      }

      const polePick = polePicks[slot.id] || '';
      const poleComp = polePick ? findComponent(polePick) : null;
      const poleLabel = poleComp ? formatComponentLabel(poleComp) : 'Default';

      const row = document.createElement('div');
      row.className = 'mht-sbs-row';
      row.setAttribute('data-mht-slot-row', slot.id);

      const labelCell = document.createElement('div');
      labelCell.className = 'mht-sbs-row__label';
      labelCell.textContent = slot.category;

      const poleCell = document.createElement('div');
      poleCell.className = 'mht-sbs-row__pole';
      poleCell.setAttribute('data-testid', 'sbs-pole-' + slot.id);
      poleCell.textContent = poleLabel;

      const draftCell = document.createElement('div');
      draftCell.className = 'mht-sbs-row__draft';

      const select = document.createElement('select');
      select.name = 'spec_cc_' + slot.id;
      select.setAttribute('data-mht-slot-select', slot.id);
      select.setAttribute('data-pole-value', polePick);

      const keepOpt = document.createElement('option');
      keepOpt.value = polePick || '';
      keepOpt.textContent = polePick ? 'Keep Pole\'s pick' : 'Keep default';
      select.appendChild(keepOpt);

      options.forEach(function (comp) {
        const opt = document.createElement('option');
        opt.value = comp.id;
        opt.textContent = formatComponentLabel(comp);
        if (polePick && comp.id === polePick) opt.disabled = true;  // already the "keep" option
        select.appendChild(opt);
      });

      // Match the row's draft to the catalog selection if a prior pass
      // populated it. fd preserves customComponents on submit.
      select.addEventListener('change', function () {
        if (select.value !== polePick) {
          row.classList.add('is-changed');
        } else {
          row.classList.remove('is-changed');
        }
        recomputePriceFor(form);
      });

      draftCell.appendChild(select);
      row.appendChild(labelCell);
      row.appendChild(poleCell);
      row.appendChild(draftCell);
      rowsEl.appendChild(row);
      renderedAnyRow = true;
    });

    if (!renderedAnyRow) {
      const empty = document.createElement('div');
      empty.className = 'mht-sbs-empty';
      empty.textContent = 'No swap-eligible components for this travel/wheel combination.';
      rowsEl.appendChild(empty);
    }
  }

  function formatComponentLabel(comp) {
    if (!comp) return '';
    if (comp.brand) return comp.brand + ' ' + comp.label;
    return comp.label || comp.id;
  }

  /** Wrapper used by component-row select change handlers — looks up the
   *  per-form recompute via the form element rather than a closed-over
   *  reference (the row controls are appended after initSideBySide
   *  returned). */
  function recomputePriceFor(form) {
    // Re-run the init's recomputePrice by dispatching a no-op input event
    // on a frame control — the handler is bound there.
    const probe = form.querySelector('[data-mht-sbs-draft="color"]');
    if (probe) {
      probe.dispatchEvent(new Event('change', { bubbles: false }));
    }
  }

  // ---------------------------------------------------------------------------
  // Bootstrap — bind handlers to every timeline on the page. Storefront
  // pages are server-rendered, so this runs once at DOMContentLoaded; we
  // don't need a MutationObserver. If a future SPA-style nav inserts
  // timelines dynamically, this can be re-run with the new root.
  // ---------------------------------------------------------------------------
  function bind(root) {
    // T-131c — comment-form + suggest-form + suggest-changes/suggest-cancel
    // bindings poistettu (Leon päätös 2026-05-27, counter/comment-flow
    // karsittu). Yllä olevat handler-funktiot (handleCommentSubmit,
    // handleSuggestSubmit, handleSuggestToggle, handleSuggestCancel,
    // saveSuggestDraft, rebuildComponentRows, jne.) ovat nyt dead-code-
    // tilassa: niillä ei ole call-site:ja eikä DOM enää sisällä targeting-
    // selektoreja (`[data-mht-comment-form]`, `[data-mht-suggest-form]`,
    // `[data-mht-action="suggest-changes"]` jne. eivät ole `my-hiisi-
    // versions-timeline.liquid`-renderin tuotosta enää). Funktiot poistetaan
    // erillisessä T-132-cleanup-PR:ssä jotta T-131c-diff säilyy fokusoituna.
    root.querySelectorAll('[data-mht-approve-form]').forEach(function (form) {
      form.addEventListener('submit', handleApproveSubmit);
    });
    // T-097 — wire the approve-modal once per timeline. One modal per
    // approve form (siblings in the .mht-nego-actions wrapper). Idempotent
    // via a data-* sentinel so re-binding from a SPA re-init doesn't
    // double-attach listeners.
    root.querySelectorAll('[data-mht-approve-modal]').forEach(wireApproveModal);
    // T-074f: per-timeline unseen-comment highlight + IntersectionObserver.
    // Säilyy: Pole-staff:n internal comments voivat näkyä read-only
    // (T-131b suodattaa customer-author-kommentit pois Liquid-tasolla).
    initUnseenComments(root);
  }

  function bindAll() {
    document
      .querySelectorAll('[data-mht-timeline]')
      .forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  // Expose for tests / future SPA-style usage. Not a public contract.
  window.__hiisiVersionsBindAll = bindAll;
})();
